import { Trans, useLingui } from "@lingui/react/macro";
import { addRelative, patientKeys } from "@/api/Patients";
import Form from "@/components/Form/Form";
import InputField from "@medoma/components/InputField/InputField";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deducedError } from "@/Utils/ErrorUtils";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import Checkbox from "@/components/Checkbox/Checkbox";
import { generateRandomUUID } from "@/Utils/UniqueId";
import { FilledButton } from "@medoma/components/Button/Button";
import type { IFormRelative, IRelative } from "@medoma/models/patients";
import { standardizePhoneNumber } from "@medoma/models/phoneNumbers";
import { useUserProfile } from "@/api/Users";

export const AddRelative = ({
  onSuccess,
  patientId,
}: {
  onSuccess: () => void;
  patientId: string;
}) => {
  const { t } = useLingui();
  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm<IFormRelative>();

  const queryClient = useQueryClient();
  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (relative: IRelative) => addRelative(patientId, relative),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: patientKeys.detail(patientId),
      });
      onSuccess();
    },
  });

  const { countryCode } = useUserProfile();

  const validateAndSubmit = handleSubmit((formData) => {
    const relative = {
      ...formData,
      id: generateRandomUUID(),
      phoneNumber: formData.phoneNumber
        ? standardizePhoneNumber({
            countryCode,
            phoneNumber: formData.phoneNumber,
          })
        : null,
    };
    mutate(relative);
  });

  return (
    <Form onSubmit={validateAndSubmit}>
      {isPending || isSuccess ? (
        <Loading message={t`Lägger till anhörig`} />
      ) : (
        <>
          {errors.root?.server?.message && !isDirty ? (
            <ErrorMessage message={errors.root.server.message} />
          ) : null}
          <Form.Row>
            <InputField
              label={t`Namn`}
              showOptionalLabel={false}
              errorMessage={errors.name?.message}
              {...register("name", {
                required: {
                  value: true,
                  message: t`Ange den anhörigas namn`,
                },
              })}
            />
            <InputField
              label={t`Telefonnummer`}
              showOptionalLabel={true}
              errorMessage={errors.phoneNumber?.message}
              {...register("phoneNumber", {
                pattern: {
                  value: /^([0-9]|\s)+$/,
                  message: t`Telefonnumret får bara innehålla siffror`,
                },
              })}
            />
            <InputField
              label={t`Relation`}
              showOptionalLabel={false}
              errorMessage={errors.relation?.message}
              {...register("relation", {
                required: {
                  value: true,
                  message: t`Ange relationen till patienten`,
                },
              })}
            />
            <Checkbox
              label={{ text: t`Sammanboende` }}
              errorMessage={errors.isLivingTogether?.message}
              {...register("isLivingTogether")}
            />
          </Form.Row>
          <FilledButton type="submit">
            <Trans>Registrera anhörig</Trans>
          </FilledButton>
        </>
      )}
    </Form>
  );
};
